import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { defectService } from "../../services/defectService";
import toast from "react-hot-toast";
import { InlineLoadSkeleton } from "../common/InlineLoadSkeleton";
import { Skeleton } from "../common/Skeleton";

/**
 * Modal to search and link an existing defect to a test case / test run / test step.
 * Supports single link (entityType + entityId) or bulk link (linkTargets array).
 * @param {boolean} open
 * @param {function} onClose
 * @param {string} clientId
 * @param {string} projectId
 * @param {string} entityType - 'test_case' | 'test_run' | 'test_step'
 * @param {string} entityId - UUID of the entity (used when linkTargets not provided)
 * @param {Array<{entity_type: string, entity_id: string}>} linkTargets - optional; bulk link one defect to many entities
 * @param {function} onSuccess - called after link succeeds
 * @param {function} onCreateNew - optional; open Push New Defect modal instead
 */
export default function LinkDefectModal({
  open,
  onClose,
  clientId,
  projectId,
  entityType,
  entityId,
  linkTargets = null,
  onSuccess,
  onCreateNew,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linkingId, setLinkingId] = useState(null);
  const targets =
    linkTargets && linkTargets.length > 0
      ? linkTargets
      : entityType && entityId
        ? [{ entity_type: entityType, entity_id: entityId }]
        : [];

  useEffect(() => {
    if (!open || !clientId || !projectId) return;
    setResults([]);
    if (query.trim()) {
      setLoading(true);
      defectService
        .search({
          client_id: clientId,
          project_id: projectId,
          query: query.trim(),
          limit: 20,
        })
        .then((res) => {
          const data = res?.data ?? res;
          setResults(data?.defects ?? []);
        })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    } else {
      defectService
        .search({ client_id: clientId, project_id: projectId, limit: 20 })
        .then((res) => {
          const data = res?.data ?? res;
          setResults(data?.defects ?? []);
        })
        .catch(() => setResults([]));
    }
  }, [open, clientId, projectId, query]);

  const handleLink = async (defectId) => {
    if (targets.length === 0) {
      toast.error("Entity context is required to link");
      return;
    }
    setLinkingId(defectId);
    try {
      for (const t of targets) {
        await defectService.link(defectId, t.entity_type, t.entity_id, {
          client_id: clientId,
          project_id: projectId,
        });
      }
      toast.success(
        targets.length > 1
          ? `Defect linked to ${targets.length} items`
          : "Defect linked",
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to link defect");
    } finally {
      setLinkingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Link Defect</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 flex-1 min-h-0">
          <Input
            placeholder="Search by title, internal ID, or external ID..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {onCreateNew && (
            <Button variant="outline" onClick={onCreateNew}>
              Create New Defect
            </Button>
          )}
          <div className="border rounded-md overflow-auto min-h-[200px]">
            {loading ? (
              <div className="p-4 space-y-3">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : results.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground text-center">
                No defects found. Try a different search or create a new defect.
              </div>
            ) : (
              <ul className="divide-y">
                {results.map((d) => (
                  <li
                    key={d.id}
                    className="flex items-center justify-between p-3 hover:bg-muted/50"
                  >
                    <div>
                      <span className="font-mono text-xs text-muted-foreground mr-2">
                        {d.internal_id}
                      </span>
                      {d.external_id && (
                        <span className="font-mono text-xs text-muted-foreground mr-2">
                          {d.external_id}
                        </span>
                      )}
                      <span className="text-sm">{d.title}</span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleLink(d.id)}
                      disabled={linkingId !== null}
                    >
                      {linkingId === d.id ? (
                        <InlineLoadSkeleton className="h-4 w-4" />
                      ) : (
                        "Link"
                      )}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
